import { Link } from 'react-router-dom';
import { ARTICLES, getFeatured, getRest } from '../data/articles';
import Seo from '../components/Seo';
import { ArrowRight, Clock } from 'lucide-react';
import PaperBale from '../components/PaperBale';
import { useMagnetic } from '../hooks/useMagnetic';

export default function Guides() {
    const featured = getFeatured();
    const rest = getRest();
    const ctaRef = useMagnetic(0.25, 8);

    return (
        <div data-testid="guides-page">
            <Seo
                title="Guías sobre Papel Grado Alimenticio | Carpepel"
                description="Guías prácticas para negocios de comida: qué es el papel grado alimenticio, cómo elegir el gramaje correcto y cuánto papel comprar cada mes."
                canonicalPath="/guias"
                schema={{
                    '@context': 'https://schema.org',
                    '@type': 'Blog',
                    name: 'Guías Carpepel',
                    description: 'Guías prácticas sobre papel grado alimenticio para negocios de alimentos en México.',
                    url: 'https://www.carpepel.com/guias',
                    blogPost: ARTICLES.map((a) => ({
                        '@type': 'BlogPosting',
                        headline: a.title,
                        description: a.excerpt,
                        datePublished: a.published,
                        url: `https://www.carpepel.com/guias/${a.slug}`,
                        author: { '@type': 'Organization', name: 'Carpepel' },
                    })),
                }}
            />

            {/* Encabezado — mismo lenguaje que el resto del sitio */}
            <div style={{ backgroundColor: 'var(--ink)' }} className="w-full py-8 relative overflow-hidden">
                <PaperBale
                    variant="line"
                    color="var(--kraft)"
                    className="pointer-events-none absolute -right-6 -top-8 h-32 w-32 opacity-[0.14] md:h-44 md:w-44"
                />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-5">
                    <div className="hidden md:block w-[3px] self-stretch rounded-full" style={{ backgroundColor: 'var(--kraft-deep)', minHeight: '48px' }} />
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-dim)' }}>
                            Conocimiento del oficio
                        </p>
                        <h1 className="font-heading text-2xl sm:text-3xl font-black text-white leading-none">
                            GUÍAS <span style={{ color: 'var(--kraft-deep)' }}>PRÁCTICAS</span>
                        </h1>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <p className="text-[var(--ink-soft)] max-w-xl mb-14 leading-relaxed text-[15px]">
                    Lo que hemos aprendido distribuyendo papel a negocios de alimentos, escrito
                    sin tecnicismos. Para que elijas bien, no para vendértelo.
                </p>

                {/* Destacado — ancla editorial, distinta jerarquía al resto */}
                {featured && (
                    <Link
                        to={`/guias/${featured.slug}`}
                        data-testid={`guide-featured-${featured.slug}`}
                        className="group block mb-16 md:mb-20"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
                            <div className="md:col-span-2">
                                <span className="font-mono text-[11px] uppercase tracking-widest" style={{ color: 'var(--kraft-deep)' }}>
                                    01 — {featured.category}
                                </span>
                            </div>
                            <div className="md:col-span-10">
                                <h2 className="font-heading text-3xl sm:text-5xl md:text-6xl font-black text-[var(--ink)] leading-[0.95] tracking-tight mb-5 text-balance group-hover:text-[var(--ink)] transition-colors duration-300">
                                    {featured.title}
                                </h2>
                                <p className="text-[var(--ink-soft)] text-base sm:text-lg leading-relaxed max-w-2xl mb-5">
                                    {featured.excerpt}
                                </p>
                                <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--kraft-deep)' }}>
                                    Leer guía
                                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                </span>
                            </div>
                        </div>
                    </Link>
                )}

                {/* Resto — lista editorial con reglas horizontales, no tarjetas */}
                <div className="border-t border-[var(--line)]">
                    {rest.map((article, i) => (
                        <Link
                            key={article.slug}
                            to={`/guias/${article.slug}`}
                            data-testid={`guide-item-${article.slug}`}
                            className="reveal-on-scroll group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-10 items-baseline py-8 md:py-10 border-b border-[var(--line)] hover:bg-[var(--paper-2)]/60 transition-colors duration-300"
                        >
                            <div className="md:col-span-2">
                                <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--ink-soft)]">
                                    {String(i + 2).padStart(2, '0')} — {article.category}
                                </span>
                            </div>
                            <div className="md:col-span-7">
                                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[var(--ink)] leading-tight mb-2 text-balance group-hover:text-[var(--kraft-deep)] transition-colors duration-300">
                                    {article.title}
                                </h3>
                                <p className="text-sm text-[var(--ink-soft)] leading-relaxed max-w-xl">{article.excerpt}</p>
                            </div>
                            <div className="md:col-span-3 flex md:justify-end items-center gap-4">
                                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[var(--ink-soft)] whitespace-nowrap">
                                    <Clock className="w-3 h-3" /> {article.readingMinutes} min
                                </span>
                                <ArrowRight className="w-4 h-4 text-[var(--ink-dim)] transition-all duration-300 group-hover:text-[var(--kraft-deep)] group-hover:translate-x-1" />
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Cierre — enlaza de vuelta al catálogo (internal linking) */}
                <div style={{ backgroundColor: 'var(--ink)' }} className="on-ink mt-16 md:mt-20 rounded-sm px-8 py-10 md:px-12 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <h2 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-wide text-white mb-2">
                            ¿Ya sabes qué papel necesitas?
                        </h2>
                        <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>
                            Revisa el catálogo completo con gramajes, medidas y precios.
                        </p>
                    </div>
                    <Link
                        ref={ctaRef}
                        to="/catalogo"
                        data-testid="guides-to-catalog"
                        className="magnetic inline-flex items-center gap-2 h-12 px-8 rounded-sm uppercase font-bold tracking-widest text-sm bg-[var(--kraft)] text-[var(--ink)] hover:opacity-85 whitespace-nowrap"
                    >
                        Ver catálogo <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
